import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { userService } from '../services/api';
import TRADUCCIONES from '../locales';

const IdiomaContext = createContext();

export const IdiomaProvider = ({ children, userIdioma, projectIdioma }) => {
  // Prioridad: preferencia explícita del usuario > elección manual guardada >
  // idioma por defecto del proyecto > español.
  const [idioma, setIdiomaState] = useState(() => {
    return userIdioma || localStorage.getItem('idioma_preferido') || projectIdioma || 'es';
  });
  // Marca si el usuario ya eligió idioma explícitamente (cuenta o clic en header).
  // Si no, el idioma sigue al default del proyecto.
  const [eleccionExplicita, setEleccionExplicita] = useState(
    () => !!(userIdioma || localStorage.getItem('idioma_preferido'))
  );
  // Espejo en ref: los efectos de los hijos corren ANTES que los del padre, así
  // que si alguien elige idioma durante el montaje (p. ej. PaisContext al fijar
  // el idioma del país), el efecto del idioma por defecto vería todavía el
  // estado viejo y lo revertiría. La ref siempre trae el valor actual.
  const explicitaRef = useRef(eleccionExplicita);
  const marcarExplicita = useCallback(() => {
    explicitaRef.current = true;
    setEleccionExplicita(true);
  }, []);

  // Sync when user idioma changes (login/logout)
  useEffect(() => {
    if (userIdioma) {
      setIdiomaState(userIdioma);
      marcarExplicita();
      localStorage.setItem('idioma_preferido', userIdioma);
    }
  }, [userIdioma]);

  // Si el proyecto define idioma por defecto y el usuario NO eligió explícitamente,
  // aplicar el del proyecto (ej. proyecto de Brasil arranca en portugués).
  useEffect(() => {
    if (projectIdioma && !explicitaRef.current) {
      setIdiomaState(projectIdioma);
    }
  }, [projectIdioma, eleccionExplicita]);

  const t = useCallback((key) => {
    const entry = TRADUCCIONES[key];
    if (!entry) return key;
    return entry[idioma] || entry['es'] || key;
  }, [idioma]);

  const setIdioma = useCallback((lang) => {
    setIdiomaState(lang);
    marcarExplicita(); // elección manual: ya no seguir el default del proyecto
    localStorage.setItem('idioma_preferido', lang);
    // Parchar el blob 'user' que AuthContext relee al recargar — si no, su
    // idioma_preferido viejo gana en el init del provider y revierte el cambio.
    try {
      const blob = localStorage.getItem('user');
      if (blob) {
        const u = JSON.parse(blob);
        if (u && typeof u === 'object') {
          u.idioma_preferido = lang;
          localStorage.setItem('user', JSON.stringify(u));
        }
      }
    } catch (_) { /* ignore */ }
    // Persistir en la cuenta solo si hay sesión: un visitante sin token dispararía
    // un 401 inútil en cada cambio de idioma (p. ej. al elegir país en el header).
    if (localStorage.getItem('token')) {
      userService.updateIdioma(lang)?.catch(() => {});
    }
  }, [marcarExplicita]);

  return (
    <IdiomaContext.Provider value={{ idioma, setIdioma, t }}>
      {children}
    </IdiomaContext.Provider>
  );
};

export const useIdioma = () => {
  const ctx = useContext(IdiomaContext);
  if (!ctx) throw new Error('useIdioma must be used within IdiomaProvider');
  return ctx;
};

export default IdiomaContext;
